import { useEffect, useState } from 'react';
import generatePDF, { Options, Resolution } from 'react-to-pdf';
import TechnicalIndicatorsPdfUi from '../../components/userInterfaces/technical-indicators/technical-indicators-pdf.ui';
import { useLocation } from 'react-router-dom';
import { TechnicalIndicatorDto, TypeTechnicalIndicator } from '../../services/dtos/technical-indicator.dto';
import { Typography } from '@mui/material';
import { MandateEstablishmentDto } from '../../services/dtos/mandate-establishment-dto';
import { HeadCellDto } from '../../services/dtos/head-cell.dto';
import { IndicatorValuesDto, IndicatorValuesFacturesDto } from '../../services/dtos/indicator-values.dto';
import { getLabelChart } from '../../components/userInterfaces/technical-indicators/technical-indicators-data.ui';

const options: Options = {
    filename: 'export_graph.pdf',
    // default is `save`
    method: 'build',
    // default is Resolution.MEDIUM = 3, which should be enough, higher values
    // increases the image quality but also the size of the PDF, so be careful
    // using values higher than 10 when having multiple pages generated, it
    // might cause the page to crash or hang.
    resolution: Resolution.MEDIUM,
    // Customize any value passed to the jsPDF instance and html2canvas
    // function. You probably will not need this and things can break,
    // so use with caution.
    overrides: {
        // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
        pdf: {
            compress: true,
        },
        // see https://html2canvas.hertzen.com/configuration for more options
        canvas: {
            useCORS: true,
        },
    },
};

/**
 * Function that generates a PDF page with technical indicators.
 * The PDF page is generated using the values from the `graph` and `mandate` state variables.
 * The generated PDF is opened in a new globalThis.
 *
 * @return {void}
 */
export default function TechnicalIndicatorsPdfPage() {
    // Location
    const location = useLocation();
    // Données
    const [graph, setGraph] = useState<TechnicalIndicatorDto | null>(null);
    // Mandat
    const [mandate, setMandate] = useState<MandateEstablishmentDto | null>(null);
    // Headers du tableau
    const [headCells, setHeadCells] = useState<HeadCellDto<IndicatorValuesDto, keyof IndicatorValuesDto>[]>();
    // Sous-titre du document
    const [subtitle, setSubtitle] = useState<string | null>();

    // Enregistrement des informations
    useEffect(() => {
        const state = localStorage.getItem('state');
        initGraph(state, setGraph, setMandate, setHeadCells, setSubtitle);
    }, [location]);

    // Génération du PDF
    useEffect(() => {
        if (graph && mandate) {
            const timesMs = 50;
            delay(timesMs).finally(() => {
                generatePDF(() => document.getElementById('indicator-' + graph.id), options)
                    .then(response => {
                        if (response) {
                            const fileURL = URL.createObjectURL(response.output('blob'));
                            globalThis.open(fileURL, '_self');
                        }
                    })
                    .finally(() => {
                        localStorage.removeItem('state');
                        document.getElementById('indicator-' + graph.id)?.remove();
                    });
            });
        }
    }, [graph, mandate]);

    //Méthode qui permet d'attendre une certain temps
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    return mandate && graph && headCells ? (
        <TechnicalIndicatorsPdfUi chartValues={graph} headCells={headCells} mandate={mandate} subtitle={subtitle} />
    ) : (
        <Typography>Merci de retélécharger le PDF.</Typography>
    );
}

/**
 * Init les graphiques
 * @param state state passé en via le local storage
 * @param setGraph setGraph
 * @param setMandate setMandate
 * @param setHeadCells setHeadCells
 * @param setSubtitle setSubtitle
 */
function initGraph(
    state: string | null,
    setGraph: (value: TechnicalIndicatorDto | null) => void,
    setMandate: (value: MandateEstablishmentDto | null) => void,
    setHeadCells: (value: HeadCellDto<IndicatorValuesDto, keyof IndicatorValuesDto>[]) => void,
    setSubtitle: (value: string | null) => void
) {
    if (state) {
        const data = JSON.parse(state);
        setSubtitle(data.title);
        setMandate(data.mandate);
        setGraph(data.graph);
        // Si le graphe concerne la facture reelle théorique, on a deux valeur, donc un traitement différent de la donnée
        if (data.graph.type === TypeTechnicalIndicator.COMPARETHEORETICALREAL) {
            const tabForValues: Record<string, Partial<IndicatorValuesFacturesDto>> = {};
            const values: IndicatorValuesFacturesDto[] = [];
            data.graph.values.forEach((currentValue: { key: string; value: string; label: string }) => {
                tabForValues[currentValue.label] = {
                    ...tabForValues[currentValue.label],
                    [currentValue.key]: currentValue.value,
                };
            });
            for (const key in tabForValues) {
                values.push({
                    label: key,
                    theorique: tabForValues[key].theorique ?? '',
                    reel: tabForValues[key].reel ?? '',
                });
            }
            data.graph.values = values;
            const headTable: HeadCellDto<IndicatorValuesFacturesDto, keyof IndicatorValuesFacturesDto>[] = [
                { id: 'label', label: 'Date' },
                { id: 'reel', label: `Réel facture` },
                { id: 'theorique', label: `Théorique facturé` },
            ];
            setHeadCells(headTable);
        } else {
            const headTable: HeadCellDto<IndicatorValuesDto, keyof IndicatorValuesDto>[] = [
                { id: 'label', label: 'Date' },
                { id: 'value', label: `${getLabelChart(data.graph.type)} (en ${data.graph.unit})` },
            ];
            setHeadCells(headTable);
        }
    }
}
